import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const r = await login(email, password);
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      toast.error(r.error);
      return;
    }
    toast.success("Welcome back");
    navigate(r.user.role === "business" ? "/dashboard" : "/");
  };

  const fillDemo = () => {
    setEmail("salon@demo.com");
    setPassword("demo1234");
  };

  return (
    <div className="mx-auto grid min-h-[calc(100vh-56px)] max-w-[1400px] grid-cols-1 md:grid-cols-2">
      <div className="hidden border-r border-border bg-grid p-12 md:block">
        <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
          → for businesses
        </p>
        <h2 className="mt-4 text-4xl leading-tight tracking-tight">
          Fill the empty chair
          <br />
          before it costs you.
        </h2>
        <p className="mt-4 max-w-sm text-sm text-muted-foreground">
          Post an instant opening in 15 seconds. We put it on the map for people
          nearby right now. Approve or decline manually — you're in control.
        </p>
        <div className="mt-10 grid grid-cols-2 gap-4 max-w-sm">
          <div className="border border-border p-4">
            <p className="mono text-3xl">30s</p>
            <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              To post
            </p>
          </div>
          <div className="border border-border p-4">
            <p className="mono text-3xl">0%</p>
            <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Commission
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-sm">
          <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
            Log in
          </p>
          <h1 className="mt-2 text-4xl leading-tight tracking-tight">
            Welcome back.
          </h1>
          <div className="mt-8 grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="email" className="mono text-[10px] uppercase tracking-[0.2em]">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
                className="rounded-none"
                placeholder="you@business.com"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="password" className="mono text-[10px] uppercase tracking-[0.2em]">
                Password
              </Label>
              <PasswordInput
                id="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-none"
                placeholder="••••••••"
              />
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="mono text-[10px] uppercase tracking-[0.2em] text-[#8A7333] hover:text-[#C9A85F] transition-colors"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>
            {error && (
              <p data-testid="login-error" className="mono text-xs text-[#F87171]">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={busy}
              className="mt-2 w-full bg-foreground text-background px-4 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-[#C9A85F] active:scale-[0.98] transition-colors disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <button
              type="button"
              data-testid="fill-demo-btn"
              onClick={fillDemo}
              className="mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground hover:text-foreground"
            >
              Use demo business credentials
            </button>
          </div>
          <p className="mt-8 text-sm text-muted-foreground">
            New here?{" "}
            <Link
              to="/register"
              data-testid="login-to-register-link"
              className="text-foreground underline underline-offset-4 hover:text-[#C9A85F]"
            >
              Create an account
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
